import { getFunctions, httpsCallable } from 'firebase/functions';
import { app, auth } from '../config/firebase';

const functions = getFunctions(app, 'us-central1');

const getR2RequestContext = async () => {
  const workerUrl = `${process.env.REACT_APP_CANVAS_R2_WORKER_URL || ''}`
    .trim()
    .replace(/\/+$/, '');
  if (!workerUrl) {
    throw new Error('The Cloudflare R2 Worker URL is not configured.');
  }
  await auth.authStateReady?.();
  const user = auth.currentUser;
  if (!user) throw new Error('Sign in before accessing Cloudflare R2 images.');
  return { workerUrl, idToken: await user.getIdToken() };
};

export const generateCanvasImage = async ({
  prompt,
  size = '1024x1024',
  referenceImages = [],
  mask = null,
  model = 'gpt-image-2.5-flare',
  quality = 'medium',
  background = 'auto',
  variations = 1
}) => {
  const callable = httpsCallable(functions, 'generateCanvasImage', {
    timeout: 300000
  });
  const response = await callable({
    prompt,
    size,
    referenceImages,
    mask,
    model,
    quality,
    background,
    variations
  });
  return response.data;
};

export const saveCanvasImageToR2 = async (imageDataUrl, filename = 'canvas-image.png') => {
  const [{ workerUrl, idToken }, imageResponse] = await Promise.all([
    getR2RequestContext(),
    fetch(imageDataUrl)
  ]);
  if (!imageResponse.ok) throw new Error('The selected image could not be prepared.');
  const imageBlob = await imageResponse.blob();
  const response = await fetch(`${workerUrl}/canvas-images`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': imageBlob.type || 'image/png',
      'X-Filename': encodeURIComponent(filename)
    },
    body: imageBlob
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(result.error || 'The image could not be saved to Cloudflare R2.');
  }
  return result;
};

export const loadCanvasImagesFromR2 = async () => {
  const { workerUrl, idToken } = await getR2RequestContext();
  const authorization = { Authorization: `Bearer ${idToken}` };
  const listResponse = await fetch(`${workerUrl}/canvas-images`, {
    headers: authorization,
    cache: 'no-store'
  });
  const list = await listResponse.json().catch(() => ({}));
  if (!listResponse.ok) {
    throw new Error(list.error || 'Saved images could not be loaded from Cloudflare R2.');
  }

  const loaded = [];
  const images = Array.isArray(list.images) ? list.images : [];
  for (let index = 0; index < images.length; index += 6) {
    const batch = images.slice(index, index + 6);
    const results = await Promise.all(batch.map(async (image) => {
      const response = await fetch(
        `${workerUrl}/canvas-images/object?key=${encodeURIComponent(image.objectKey)}`,
        { headers: authorization, cache: 'no-store' }
      );
      if (!response.ok) return null;
      return { ...image, blob: await response.blob() };
    }));
    loaded.push(...results.filter(Boolean));
  }
  return loaded;
};

export const deleteCanvasImageFromR2 = async (objectKey) => {
  const { workerUrl, idToken } = await getR2RequestContext();
  const response = await fetch(
    `${workerUrl}/canvas-images/object?key=${encodeURIComponent(objectKey)}`,
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${idToken}` },
      cache: 'no-store'
    }
  );
  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(result.error || 'The image could not be removed from Cloudflare R2.');
  }
  return result;
};

export const saveCanvasProjectToR2 = async ({ title, canvas, objectKey = null }) => {
  const { workerUrl, idToken } = await getR2RequestContext();
  const response = await fetch(`${workerUrl}/canvas-projects`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ title, canvas, objectKey })
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(result.error || 'The canvas project could not be saved.');
  return result;
};

export const listCanvasProjectsFromR2 = async () => {
  const { workerUrl, idToken } = await getR2RequestContext();
  const response = await fetch(`${workerUrl}/canvas-projects`, {
    headers: { Authorization: `Bearer ${idToken}` },
    cache: 'no-store'
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(result.error || 'Saved canvas projects could not be loaded.');
  return Array.isArray(result.projects) ? result.projects : [];
};

export const loadCanvasProjectFromR2 = async (objectKey) => {
  const { workerUrl, idToken } = await getR2RequestContext();
  const response = await fetch(
    `${workerUrl}/canvas-projects/object?key=${encodeURIComponent(objectKey)}`,
    {
      headers: { Authorization: `Bearer ${idToken}` },
      cache: 'no-store'
    }
  );
  const result = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(result.error || 'The canvas project could not be loaded.');
  return result;
};
